import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  Wallet,
  Users,
  UserPlus,
  FileText,
  Tags,
  SlidersHorizontal,
  BarChart2,
} from "lucide-react";
import { PortalDsButton } from "src/components/portal-design-system/portal-ds-button";
import { portalDs } from "src/components/portal-design-system/portal-ds-classes";
import { RootState } from "src/redux/redux-store";
import { canSyncInvoice } from "src/pages/billing/utils/billing-utils";
import notificationService from "src/services/notification/notification.service";
import { cn } from "src/lib/utils";
import { useLoadCommissionInvoicesMutation } from "./endpoints/commissions.endpoints";
import LoadCommissionInvoicesModal from "./components/load-commission-invoices-modal";
import {
  CommissionViewPeriodProvider,
  useCommissionViewPeriod,
} from "./commission-view-period-context";
import type {
  CommissionViewType,
  LegacyCommissionViewType,
  LoadCommissionInvoicesRequest,
} from "./types/commissions.types";
import ActivationTeamView from "./views/activation-team-view";
import InternalReferralsView from "./views/internal-referrals-view";
import ExternalRepsView from "./views/external-reps-view";
import InvoicesView from "./views/invoices-view";
import SkuCategoriesView from "./views/sku-categories-view";
import AdjustmentsView from "./views/adjustments-view";
import ReportsView from "./views/reports-view";

const COMMISSION_VIEWS: { id: CommissionViewType; label: string; icon: React.ElementType }[] = [
  { id: "activation-team", label: "Activation Team", icon: Wallet },
  { id: "internal-referrals", label: "Internal Referrals", icon: UserPlus },
  { id: "external-reps", label: "External Reps", icon: Users },
  { id: "invoices", label: "Paid Invoices", icon: FileText },
  { id: "sku-categories", label: "SKU Categories", icon: Tags },
  { id: "adjustments", label: "Adjustments", icon: SlidersHorizontal },
  { id: "reports", label: "Reports", icon: BarChart2 },
];

const LEGACY_VIEW_REDIRECTS: Record<string, CommissionViewType> = {
  payouts: "activation-team",
  referrals: "external-reps",
  "gross-margin": "reports",
};

const VALID_VIEW_IDS = COMMISSION_VIEWS.map((v) => v.id);
const DEFAULT_VIEW: CommissionViewType = "activation-team";

function resolveView(viewParam: string | null): CommissionViewType {
  if (viewParam && LEGACY_VIEW_REDIRECTS[viewParam]) {
    return LEGACY_VIEW_REDIRECTS[viewParam];
  }
  if (viewParam && VALID_VIEW_IDS.includes(viewParam as CommissionViewType)) {
    return viewParam as CommissionViewType;
  }
  return DEFAULT_VIEW;
}

const CommissionsTabContent: React.FC = () => {
  const dispatch = useDispatch();
  const groups = useSelector((state: RootState) => state.userState.permissions?.groups ?? []);
  const isBillingAdmin = canSyncInvoice(groups);
  const [loadModalOpen, setLoadModalOpen] = useState(false);
  const [loadCommissionInvoices] = useLoadCommissionInvoicesMutation();
  const { period: viewPeriod } = useCommissionViewPeriod();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get("view");
  const currentView = resolveView(viewParam);

  useEffect(() => {
    if (!viewParam) return;
    const redirected = LEGACY_VIEW_REDIRECTS[viewParam as LegacyCommissionViewType];
    if (redirected && redirected !== viewParam) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("view", redirected);
        return next;
      });
    }
  }, [viewParam, setSearchParams]);

  const setView = (viewId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("view", viewId);
      return next;
    });
  };

  const handleLoadCommissionInvoices = async (payload: LoadCommissionInvoicesRequest) => {
    try {
      const result = await loadCommissionInvoices(payload).unwrap();
      notificationService.handleSuccess(
        result,
        dispatch,
        result.message || "Commission invoices loaded successfully"
      );
    } catch (error) {
      notificationService.handleError(error, dispatch, "Failed to load commission invoices");
      throw error;
    }
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          className={cn(portalDs.segmentGroup, "flex-wrap")}
          role="tablist"
          aria-label="Commission views"
        >
          {COMMISSION_VIEWS.map(({ id, label, icon: Icon }) => {
            const isActive = currentView === id;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={cn(
                  portalDs.segment,
                  "inline-flex items-center gap-1.5 whitespace-nowrap",
                  isActive && portalDs.segmentActive
                )}
                onClick={() => setView(id)}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            );
          })}
        </div>
        {isBillingAdmin && (
          <PortalDsButton
            type="button"
            variant="primary"
            size="sm"
            className="shrink-0"
            onClick={() => setLoadModalOpen(true)}
          >
            Update
          </PortalDsButton>
        )}
      </div>
      {isBillingAdmin && (
        <LoadCommissionInvoicesModal
          open={loadModalOpen}
          onOpenChange={setLoadModalOpen}
          onSubmit={handleLoadCommissionInvoices}
          defaultQuarter={viewPeriod?.quarter}
          defaultYear={viewPeriod?.year}
        />
      )}
      <div className="min-h-0 flex-1 overflow-auto">
        {currentView === "activation-team" && <ActivationTeamView />}
        {currentView === "internal-referrals" && <InternalReferralsView />}
        {currentView === "external-reps" && <ExternalRepsView />}
        {currentView === "invoices" && <InvoicesView />}
        {currentView === "sku-categories" && <SkuCategoriesView />}
        {currentView === "adjustments" && <AdjustmentsView />}
        {currentView === "reports" && <ReportsView />}
      </div>
    </div>
  );
};

const CommissionsTab: React.FC = () => (
  <CommissionViewPeriodProvider>
    <CommissionsTabContent />
  </CommissionViewPeriodProvider>
);

export default CommissionsTab;
